import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Check, MoonStars, SkipForward } from "@phosphor-icons/react";
import { logWarn } from "@/lib/logger";

const STEPS = [
  {
    key: "timeline",
    label: "When are you looking to make nikah?",
    hint: "This helps our matchmakers pace your shortlist.",
    options: [
      { value: "3_months", label: "Within 3 months" },
      { value: "6_months", label: "Within 6 months" },
      { value: "1_year",  label: "Within a year" },
      { value: "unhurried", label: "Whenever the time is right" },
    ],
  },
  {
    key: "community",
    label: "Which sect describes your household?",
    hint: "You may still receive introductions across sects if you choose.",
    options: [
      { value: "Sunni", label: "Sunni" },
      { value: "Shia", label: "Shia" },
      { value: "Sufi", label: "Sufi" },
      { value: "Bohra", label: "Bohra" },
      { value: "Ismaili", label: "Ismaili" },
      { value: "Ahmadiyya", label: "Ahmadiyya" },
      { value: "Other", label: "Prefer to discuss" },
    ],
  },
  {
    key: "namaz",
    label: "How often do you offer salah?",
    hint: "No judgement — we just help match on shared rhythm.",
    options: [
      { value: "Five times daily", label: "Five times daily" },
      { value: "Regularly",        label: "Regularly" },
      { value: "Sometimes",        label: "Sometimes" },
      { value: "Rarely",           label: "Rarely" },
    ],
  },
  {
    key: "observance",
    label: "Do you observe hijab / keep a beard?",
    options: [
      { value: "observant", label: "Yes, observant" },
      { value: "modest",    label: "Modest but not strict" },
      { value: "not_now",   label: "Not currently" },
      { value: "open",      label: "Open to discuss" },
    ],
  },
  {
    key: "wali_involvement",
    label: "Will your wali / family be involved from the start?",
    hint: "Muslims often prefer the family in from step one — either way is welcome here.",
    options: [
      { value: "yes",   label: "Yes, from day one" },
      { value: "soon",  label: "Soon after mutual interest" },
      { value: "later", label: "After we're both ready" },
      { value: "no",    label: "I'll handle this myself" },
    ],
  },
];

export default function Welcome() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;
  const progress = Math.round(((idx + 1) / STEPS.length) * 100);

  const choose = (v) => setAnswers((a) => ({ ...a, [step.key]: v }));
  const chosen = answers[step.key];

  const commit = async () => {
    setSaving(true);
    try {
      await api.put("/profiles/me", { ...answers, onboarding_complete: true });
      toast.success("Barakallahu feek — dossier updated");
      await refresh();
      nav("/my-profile");
    } catch (e) {
      toast.error("Save failed — please try again");
    } finally { setSaving(false); }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await api.put("/profiles/me", { onboarding_complete: true });
    } catch (e) {
      logWarn("onboarding_complete flag save failed:", e?.message || e);
    }
    setSaving(false);
    nav("/my-profile");
  };

  const next = () => {
    if (!chosen) return;
    if (isLast) return commit();
    setIdx((i) => i + 1);
  };
  const back = () => setIdx((i) => Math.max(0, i - 1));

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-14" data-testid="welcome-page">
      {/* Header */}
      <div className="text-center">
        <MoonStars size={32} weight="duotone" className="text-brand-terracotta mx-auto" />
        <div className="label-eyebrow text-brand-terracotta mt-3">Ahlan wa sahlan</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-3 text-brand-ink">
          A short <em className="text-brand-terracotta">Nikah readiness</em> check
        </h1>
        <p className="mt-4 text-brand-muted max-w-xl mx-auto leading-relaxed">
          Five gentle questions — thirty seconds. Your answers help our
          matchmakers curate meaningful, deen-aligned introductions.
        </p>
      </div>

      {/* Progress */}
      <div className="mt-10 flex items-center gap-4">
        <div className="flex-1 h-1 bg-brand-line overflow-hidden">
          <div className="h-full bg-brand-terracotta transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <span className="text-xs text-brand-muted uppercase tracking-widest num-lining">{idx + 1} / {STEPS.length}</span>
      </div>

      {/* Card */}
      <div className="mt-10 bg-white border border-brand-line p-8 md:p-12" data-testid={`step-${step.key}`}>
        <div className="label-eyebrow text-brand-muted">Question {String(idx + 1).padStart(2, "0")}</div>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 text-brand-ink">{step.label}</h2>
        {step.hint && <p className="mt-3 text-sm text-brand-muted italic">{step.hint}</p>}

        <div className="mt-8 grid gap-3">
          {step.options.map((o) => {
            const active = chosen === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => choose(o.value)}
                data-testid={`opt-${step.key}-${o.value}`}
                className={`flex items-center justify-between text-left px-5 py-4 border transition-all duration-200 ${
                  active
                    ? "border-brand-terracotta bg-brand-terracotta/5"
                    : "border-brand-line bg-white hover:border-brand-ink/40"
                }`}
              >
                <span className="text-brand-ink text-base">{o.label}</span>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  active ? "border-brand-terracotta bg-brand-terracotta" : "border-brand-line"
                }`}>
                  {active && <Check size={12} weight="bold" className="text-white" />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Nav */}
      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
        <button
          onClick={skip}
          disabled={saving}
          data-testid="welcome-skip"
          className="text-sm text-brand-muted hover:text-brand-terracotta transition-colors flex items-center gap-1"
        >
          <SkipForward size={14} /> Skip for now
        </button>
        <div className="flex gap-3">
          {idx > 0 && (
            <Button variant="outline" onClick={back} data-testid="welcome-back" className="rounded-full border-brand-line">
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
          )}
          <Button
            onClick={next}
            disabled={!chosen || saving}
            data-testid="welcome-next"
            className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-8"
          >
            {isLast ? (saving ? "Saving…" : "Complete") : "Continue"} <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-brand-muted">
        Answered <span className="num-lining">{answeredCount}</span> of <span className="num-lining">{STEPS.length}</span>. All fields can be edited later in your profile.
      </p>
    </div>
  );
}
